import { MAX_ARTICLE_SEARCH_TERM_LENGTH } from 'constants/articleResults';
import React, { FormEvent, useEffect, useState } from 'react';

import { Button, FormControl } from '@material-ui/core';
import Search from '@material-ui/icons/Search';

import ClearableTextField from 'components/ClearableTextField';

import styles from './ArticleSearchBox.module.scss';

export interface ArticleSearchBoxProps {
  initialSearchTerm?: string;
  onSearch: (searchTerm: string) => void;
}

const ArticleSearchBox: React.FC<ArticleSearchBoxProps> = ({
  initialSearchTerm = '',
  onSearch,
}) => {
  const [searchTerm, setSearchTerm] = useState(initialSearchTerm);

  // This component stays mounted across client-side navigation between /articles and
  // /articles?q=... (Back/Forward, or submitting a new search), so the initial useState seed
  // above only covers the first mount - without this, the input would keep showing a stale term
  // after the URL (and hence initialSearchTerm) changes.
  useEffect(() => {
    setSearchTerm(initialSearchTerm);
  }, [initialSearchTerm]);

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    onSearch(searchTerm.trim());
  };

  const handleClear = () => {
    setSearchTerm('');
    onSearch('');
  };

  return (
    <form className={styles.container} onSubmit={handleSubmit} role="search">
      <FormControl fullWidth className={styles.field}>
        <ClearableTextField
          id="article-search"
          name="q"
          placeholder="Search articles"
          value={searchTerm}
          hasClear
          onChange={e => setSearchTerm(e.target.value)}
          onClear={handleClear}
          InputProps={{
            disableUnderline: true,
            startAdornment: <Search className={styles.searchIcon} />,
          }}
          inputProps={{
            'data-testid': 'article-search-input',
            'aria-label': 'Search articles',
            maxLength: MAX_ARTICLE_SEARCH_TERM_LENGTH,
          }}
        />
      </FormControl>
      <Button
        type="submit"
        color="primary"
        variant="contained"
        className={styles.searchButton}
        data-testid="article-search-button"
      >
        Search
      </Button>
    </form>
  );
};

export default ArticleSearchBox;
